import { paragraphStyle, textRunStyle } from "@cva/renderer";
import {
  closestWeight,
  listMarkers,
  type MarkField,
  type MarkStyle,
  normalizeMarks,
  normalizeParagraphs,
  type ParagraphFormat,
  TEXT_MAX_LENGTH,
  type TextElement,
  type TextListKind,
  type TextMark,
  type TextStyle,
  textParagraphs,
} from "@cva/shared";
import { baseKeymap, splitBlockAs } from "prosemirror-commands";
import { history, redo, undo } from "prosemirror-history";
import { InputRule, inputRules } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import {
  type Mark,
  type MarkType,
  type Node as PMNode,
  Schema,
  type Slice,
} from "prosemirror-model";
import {
  type Command,
  EditorState,
  Plugin,
  PluginKey,
  TextSelection,
  type Transaction,
} from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";
import type { CSSProperties } from "react";
import { create } from "zustand";

/**
 * Inline rich text editing with ProseMirror. The editor's DOM matches the
 * renderer's rich text output (packages/renderer: one block per paragraph,
 * spans for formatted runs, list markers from `data-list-marker`), so the text
 * does not move when editing starts or ends. The document model is
 * scene/richtext.ts: plain `text` plus `marks` and `paragraphs`.
 */

const UNITLESS = new Set(["fontWeight", "lineHeight", "opacity", "zIndex", "flexShrink"]);

/** React style object → CSS text (for ProseMirror's DOM specs). */
export function cssText(style: CSSProperties): string {
  return Object.entries(style)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => {
      const property = key.startsWith("--")
        ? key
        : key.replace(/^Webkit/, "-webkit-").replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
      const text = typeof value === "number" && !UNITLESS.has(key) ? `${value}px` : String(value);
      return `${property}:${text}`;
    })
    .join(";");
}

/** A fake element with only the given style: run styles without element context. */
const BARE: TextElement = { style: {} as TextStyle } as TextElement;

function runSpan(style: MarkStyle, attrs: Record<string, string | undefined> = {}) {
  const css = cssText(textRunStyle(style, BARE));
  const out: Record<string, string> = { style: css };
  for (const [key, value] of Object.entries(attrs)) if (value !== undefined) out[key] = value;
  return ["span", out, 0] as const;
}

const WEIGHT_WORDS: Record<string, number> = { bold: 700, bolder: 700, normal: 400, lighter: 300 };

export const richSchema = new Schema({
  nodes: {
    doc: { content: "paragraph+" },
    paragraph: {
      content: "text*",
      attrs: { list: { default: null }, indent: { default: 0 } },
      parseDOM: [
        {
          tag: "li",
          getAttrs: (dom) => ({
            list: (dom as HTMLElement).parentElement?.tagName === "OL" ? "number" : "bullet",
            indent: 0,
          }),
        },
        {
          tag: "div[data-list-kind]",
          getAttrs: (dom) => ({
            list: (dom as HTMLElement).dataset.listKind ?? null,
            indent: Number((dom as HTMLElement).dataset.indent ?? 0) || 0,
          }),
        },
        { tag: "p" },
        { tag: "div" },
        { tag: "h1" },
        { tag: "h2" },
        { tag: "h3" },
        { tag: "h4" },
        { tag: "blockquote" },
      ],
      toDOM: (node) => {
        const format: ParagraphFormat = {
          list: node.attrs.list ?? undefined,
          indent: node.attrs.indent || undefined,
        };
        const attrs: Record<string, string> = {};
        const css = cssText(paragraphStyle(format));
        if (css) attrs.style = css;
        if (format.list) attrs["data-list-kind"] = format.list;
        if (format.indent) attrs["data-indent"] = String(format.indent);
        return ["div", attrs, 0];
      },
    },
    text: {},
  },
  marks: {
    family: {
      attrs: { value: {} },
      parseDOM: [
        {
          tag: "span[data-font-family]",
          getAttrs: (dom) => ({ value: (dom as HTMLElement).dataset.fontFamily }),
        },
      ],
      toDOM: (mark) =>
        runSpan({ fontFamily: mark.attrs.value }, { "data-font-family": mark.attrs.value }),
    },
    size: {
      attrs: { value: {} },
      parseDOM: [
        {
          tag: "span[data-font-size]",
          getAttrs: (dom) => ({ value: Number((dom as HTMLElement).dataset.fontSize) }),
        },
      ],
      toDOM: (mark) => {
        const css = `font-size:${mark.attrs.value}px;letter-spacing:var(--text-letter-spacing,normal)`;
        return ["span", { style: css, "data-font-size": String(mark.attrs.value) }, 0];
      },
    },
    weight: {
      attrs: { value: {} },
      parseDOM: [
        { tag: "b", getAttrs: () => ({ value: 700 }) },
        { tag: "strong", getAttrs: () => ({ value: 700 }) },
        {
          style: "font-weight",
          getAttrs: (value) => {
            const weight = WEIGHT_WORDS[value] ?? Number(value);
            return Number.isFinite(weight) && weight >= 100 && weight <= 900
              ? { value: Math.round(weight / 100) * 100 }
              : false;
          },
        },
      ],
      toDOM: (mark) => runSpan({ fontWeight: mark.attrs.value }),
    },
    italic: {
      attrs: { value: { default: true } },
      parseDOM: [
        { tag: "i" },
        { tag: "em" },
        { style: "font-style", getAttrs: (value) => ({ value: value === "italic" }) },
      ],
      toDOM: (mark) => runSpan({ italic: mark.attrs.value }),
    },
    color: {
      attrs: { value: {}, token: { default: null } },
      parseDOM: [
        {
          tag: "span[data-color]",
          getAttrs: (dom) => ({
            value: (dom as HTMLElement).dataset.color,
            token: (dom as HTMLElement).dataset.colorToken ?? null,
          }),
        },
      ],
      toDOM: (mark) =>
        runSpan(
          { color: mark.attrs.value },
          { "data-color": mark.attrs.value, "data-color-token": mark.attrs.token ?? undefined },
        ),
    },
    underline: {
      parseDOM: [
        { tag: "u" },
        {
          style: "text-decoration",
          getAttrs: (value) => (value.includes("underline") ? {} : false),
        },
        {
          style: "text-decoration-line",
          getAttrs: (value) => (value.includes("underline") ? {} : false),
        },
      ],
      toDOM: () => runSpan({ underline: true }),
    },
    strike: {
      parseDOM: [
        { tag: "s" },
        { tag: "del" },
        { tag: "strike" },
        {
          style: "text-decoration",
          getAttrs: (value) => (value.includes("line-through") ? {} : false),
        },
        {
          style: "text-decoration-line",
          getAttrs: (value) => (value.includes("line-through") ? {} : false),
        },
      ],
      toDOM: () => runSpan({ strike: true }),
    },
  },
});

const M = richSchema.marks as Record<
  "family" | "size" | "weight" | "italic" | "underline" | "strike" | "color",
  MarkType
>;

// ---------------------------------------------------------------------------
// Element ⇄ ProseMirror document
// ---------------------------------------------------------------------------

function marksFor(style: MarkStyle): Mark[] {
  const marks: Mark[] = [];
  if (style.fontFamily) marks.push(M.family.create({ value: style.fontFamily }));
  if (style.fontSize !== undefined) marks.push(M.size.create({ value: style.fontSize }));
  if (style.fontWeight !== undefined) marks.push(M.weight.create({ value: style.fontWeight }));
  if (style.italic !== undefined) marks.push(M.italic.create({ value: style.italic }));
  if (style.underline) marks.push(M.underline.create());
  if (style.strike) marks.push(M.strike.create());
  if (style.color)
    marks.push(M.color.create({ value: style.color, token: style.colorToken ?? null }));
  return marks;
}

function styleOf(marks: readonly Mark[]): MarkStyle {
  const style: MarkStyle = {};
  for (const mark of marks) {
    const value = mark.attrs.value;
    if (mark.type === M.family) style.fontFamily = value;
    else if (mark.type === M.size) style.fontSize = value;
    else if (mark.type === M.weight) style.fontWeight = value;
    else if (mark.type === M.italic) style.italic = value;
    else if (mark.type === M.underline) style.underline = true;
    else if (mark.type === M.strike) style.strike = true;
    else if (mark.type === M.color) {
      style.color = value;
      if (mark.attrs.token) style.colorToken = mark.attrs.token;
    }
  }
  return style;
}

export function elementToDoc(element: Pick<TextElement, "text" | "marks" | "paragraphs">): PMNode {
  return richSchema.node(
    "doc",
    null,
    textParagraphs(element).map((paragraph) =>
      richSchema.node(
        "paragraph",
        { list: paragraph.format?.list ?? null, indent: paragraph.format?.indent ?? 0 },
        paragraph.runs
          .filter((run) => run.text.length > 0)
          .map((run) => richSchema.text(run.text, marksFor(run.style))),
      ),
    ),
  );
}

export interface RichValue {
  text: string;
  marks: TextMark[] | undefined;
  paragraphs: (ParagraphFormat | null)[] | undefined;
}

export function docToValue(doc: PMNode): RichValue {
  let text = "";
  const marks: TextMark[] = [];
  const formats: (ParagraphFormat | null)[] = [];
  doc.forEach((paragraph, _offset, index) => {
    if (index > 0) text += "\n";
    formats.push({
      list: paragraph.attrs.list ?? undefined,
      indent: paragraph.attrs.indent || undefined,
    });
    paragraph.forEach((child) => {
      const from = text.length;
      text += child.text ?? "";
      const style = styleOf(child.marks);
      if (Object.keys(style).length > 0) marks.push({ from, to: text.length, ...style });
    });
  });
  const normalized = normalizeMarks(marks, text.length);
  return {
    text,
    marks: normalized.length > 0 ? normalized : undefined,
    paragraphs: normalizeParagraphs(formats, doc.childCount),
  };
}

/** Same content as the element (text, marks, paragraph formats)? */
export function sameValue(value: RichValue, element: TextElement): boolean {
  const marks = normalizeMarks(element.marks, element.text.length);
  return (
    value.text === element.text &&
    JSON.stringify(value.marks ?? []) === JSON.stringify(marks) &&
    JSON.stringify(value.paragraphs ?? []) ===
      JSON.stringify(normalizeParagraphs(element.paragraphs, element.text.split("\n").length) ?? [])
  );
}

function textLength(doc: PMNode): number {
  let length = Math.max(0, doc.childCount - 1);
  doc.forEach((paragraph) => {
    length += paragraph.textContent.length;
  });
  return length;
}

// ---------------------------------------------------------------------------
// Selection format (for the properties panel)
// ---------------------------------------------------------------------------

export interface FieldValue<T> {
  value: T | undefined;
  mixed: boolean;
}

export interface SelectionFormat {
  fontFamily: FieldValue<string>;
  fontSize: FieldValue<number>;
  fontWeight: FieldValue<number>;
  italic: FieldValue<boolean>;
  underline: FieldValue<boolean>;
  strike: FieldValue<boolean>;
  color: FieldValue<string>;
  colorToken: FieldValue<string | undefined>;
  list: FieldValue<TextListKind | null>;
  /** Selection is collapsed (changes apply to what is typed next). */
  collapsed: boolean;
}

/** Effective value of each field for a set of marks. */
function effective(style: MarkStyle, element: TextElement) {
  return {
    fontFamily: style.fontFamily ?? element.style.fontFamily,
    fontSize: style.fontSize ?? element.style.fontSize,
    fontWeight: style.fontWeight ?? element.style.fontWeight,
    italic: style.italic ?? element.style.italic,
    underline: Boolean(style.underline || element.style.underline),
    strike: Boolean(style.strike || element.style.strike),
    color: style.color ?? element.style.color,
    colorToken: style.color ? style.colorToken : element.brand?.color,
  };
}

function collect<T>(values: T[]): FieldValue<T> {
  const first = values[0];
  return { value: first, mixed: values.some((value) => value !== first) };
}

export function selectionFormat(state: EditorState, element: TextElement): SelectionFormat {
  const { from, to, empty, $from } = state.selection;
  const styles: MarkStyle[] = [];
  if (empty) {
    styles.push(styleOf(state.storedMarks ?? $from.marks()));
  } else {
    state.doc.nodesBetween(from, to, (node) => {
      if (node.isText) styles.push(styleOf(node.marks));
    });
    if (styles.length === 0) styles.push(styleOf($from.marks()));
  }
  const values = styles.map((style) => effective(style, element));
  const lists: (TextListKind | null)[] = [];
  state.doc.nodesBetween(from, Math.max(from, to), (node) => {
    if (node.type.name === "paragraph") {
      lists.push(node.attrs.list ?? null);
      return false;
    }
    return true;
  });
  return {
    fontFamily: collect(values.map((value) => value.fontFamily)),
    fontSize: collect(values.map((value) => value.fontSize)),
    fontWeight: collect(values.map((value) => value.fontWeight)),
    italic: collect(values.map((value) => value.italic)),
    underline: collect(values.map((value) => value.underline)),
    strike: collect(values.map((value) => value.strike)),
    color: collect(values.map((value) => value.color)),
    colorToken: collect(values.map((value) => value.colorToken)),
    list: collect(lists.length > 0 ? lists : [null]),
    collapsed: empty,
  };
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

/** Changes the element itself while editing (whole-text values). */
export type ElementStylePatch = (recipe: (element: TextElement) => void) => void;

export interface RichContext {
  element: TextElement;
  patchElement: ElementStylePatch;
}

type ValueField = Exclude<MarkField, "underline" | "strike">;

const FIELD_MARK: Record<MarkField, MarkType> = {
  fontFamily: M.family,
  fontSize: M.size,
  fontWeight: M.weight,
  italic: M.italic,
  underline: M.underline,
  strike: M.strike,
  color: M.color,
};

/** The selection spans all of the text. */
function coversAll(state: EditorState): boolean {
  const { from, to, empty } = state.selection;
  return !empty && from <= 1 && to >= state.doc.content.size - 1;
}

function removeEverywhere(tr: Transaction, type: MarkType): Transaction {
  return tr.removeMark(0, tr.doc.content.size, type);
}

/**
 * Sets a value field on the selection. Over the whole text it becomes the
 * element's own value (marks of that field are removed), so a fully selected
 * text stays simple; a value equal to the element's removes the mark.
 */
export function setField(
  view: EditorView,
  context: RichContext,
  field: ValueField,
  value: string | number | boolean,
  token?: string | null,
): void {
  const { state } = view;
  const type = FIELD_MARK[field];
  const { element } = context;
  if (coversAll(state)) {
    context.patchElement((target) => {
      (target.style as Record<string, unknown>)[field] = value;
      if (field === "fontFamily" && typeof value === "string") {
        target.style.fontWeight = closestWeight(value, target.style.fontWeight);
      }
      if (field === "color") {
        if (token) target.brand = { ...target.brand, color: token };
        else if (target.brand?.color) {
          const { color: _, ...rest } = target.brand;
          target.brand = rest;
        }
      }
    });
    view.dispatch(removeEverywhere(state.tr, type));
    return;
  }
  const same = element.style[field] === value && (field !== "color" || !token);
  const mark =
    field === "color" ? type.create({ value, token: token ?? null }) : type.create({ value });
  const { from, to, empty } = state.selection;
  if (empty) {
    view.dispatch(same ? state.tr.removeStoredMark(type) : state.tr.addStoredMark(mark));
    return;
  }
  view.dispatch(same ? state.tr.removeMark(from, to, type) : state.tr.addMark(from, to, mark));
}

/** Bold on/off for the selection. */
export function toggleBold(view: EditorView, context: RichContext): void {
  const format = selectionFormat(view.state, context.element);
  const bold = !format.fontWeight.mixed && (format.fontWeight.value ?? 400) >= 600;
  const family = format.fontFamily.value ?? context.element.style.fontFamily;
  setField(view, context, "fontWeight", closestWeight(family, bold ? 400 : 700));
}

export function toggleItalic(view: EditorView, context: RichContext): void {
  const format = selectionFormat(view.state, context.element);
  setField(view, context, "italic", format.italic.mixed ? true : !format.italic.value);
}

/**
 * Underline / line-through for the selection. They can only be added to the
 * element's own value, so removing one from part of a fully decorated text
 * moves it from the element onto the rest of the text first.
 */
export function toggleDecoration(
  view: EditorView,
  context: RichContext,
  field: "underline" | "strike",
): void {
  const { state } = view;
  const type = FIELD_MARK[field];
  const format = selectionFormat(state, context.element);
  const on = !format[field].mixed && Boolean(format[field].value);
  const { from, to, empty } = state.selection;
  if (coversAll(state)) {
    context.patchElement((target) => {
      target.style[field] = !on;
    });
    view.dispatch(removeEverywhere(state.tr, type));
    return;
  }
  if (!on) {
    view.dispatch(
      empty ? state.tr.addStoredMark(type.create()) : state.tr.addMark(from, to, type.create()),
    );
    return;
  }
  let tr = state.tr;
  if (context.element.style[field]) {
    context.patchElement((target) => {
      target.style[field] = false;
    });
    tr = tr.addMark(0, tr.doc.content.size, type.create());
  }
  tr = empty ? tr.removeStoredMark(type) : tr.removeMark(from, to, type);
  view.dispatch(tr);
}

function paragraphsInSelection(state: EditorState): { pos: number; node: PMNode }[] {
  const out: { pos: number; node: PMNode }[] = [];
  const { from, to } = state.selection;
  state.doc.nodesBetween(from, Math.max(from, to), (node, pos) => {
    if (node.type.name === "paragraph") {
      out.push({ pos, node });
      return false;
    }
    return true;
  });
  return out;
}

/** Turns the selected paragraphs into a list of `kind`, or back into plain paragraphs. */
export function toggleList(view: EditorView, kind: TextListKind): void {
  const { state } = view;
  const paragraphs = paragraphsInSelection(state);
  const all = paragraphs.every(({ node }) => node.attrs.list === kind);
  let tr = state.tr;
  for (const { pos, node } of paragraphs) {
    tr = tr.setNodeMarkup(pos, undefined, {
      list: all ? null : kind,
      indent: all ? 0 : node.attrs.indent,
    });
  }
  view.dispatch(tr);
}

/** Changes the nesting of the selected list items. */
function indentCommand(delta: 1 | -1): Command {
  return (state, dispatch) => {
    const paragraphs = paragraphsInSelection(state);
    if (!paragraphs.some(({ node }) => node.attrs.list)) return false;
    if (dispatch) {
      let tr = state.tr;
      for (const { pos, node } of paragraphs) {
        if (!node.attrs.list) continue;
        const indent = Math.max(0, Math.min(4, (node.attrs.indent ?? 0) + delta));
        tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent });
      }
      dispatch(tr);
    }
    return true;
  };
}

export function indentList(view: EditorView, delta: 1 | -1): void {
  indentCommand(delta)(view.state, view.dispatch);
}

/** Enter: an empty list item ends the list; otherwise split, keeping list and formatting. */
const enter: Command = (state, dispatch) => {
  const { $from, empty } = state.selection;
  const paragraph = $from.parent;
  if (empty && paragraph.attrs.list && paragraph.content.size === 0) {
    dispatch?.(state.tr.setNodeMarkup($from.before(), undefined, { list: null, indent: 0 }));
    return true;
  }
  const marks = state.storedMarks ?? ($from.parentOffset > 0 ? $from.marks() : null);
  return splitBlockAs((node) => ({ type: node.type, attrs: node.attrs }))(
    state,
    dispatch &&
      ((tr) => {
        if (marks) tr.ensureMarks(marks);
        dispatch(tr);
      }),
  );
};

/** Backspace at the start of a list item removes the list first. */
const backspace: Command = (state, dispatch) => {
  const { $from, empty } = state.selection;
  if (!empty || $from.parentOffset > 0) return false;
  const paragraph = $from.parent;
  if (!paragraph.attrs.list && !paragraph.attrs.indent) return false;
  dispatch?.(state.tr.setNodeMarkup($from.before(), undefined, { list: null, indent: 0 }));
  return true;
};

function listRule(pattern: RegExp, kind: TextListKind): InputRule {
  return new InputRule(pattern, (state, _match, start, end) => {
    const $start = state.doc.resolve(start);
    if ($start.parent.attrs.list) return null;
    return state.tr
      .delete(start, end)
      .setNodeMarkup($start.before(), undefined, { ...$start.parent.attrs, list: kind });
  });
}

// ---------------------------------------------------------------------------
// Plugins
// ---------------------------------------------------------------------------

/** List markers as node decorations (drawn by LIST_MARKER_CSS). */
const markersPlugin = new Plugin({
  props: {
    decorations(state) {
      const formats: (ParagraphFormat | null)[] = [];
      state.doc.forEach((node) => {
        formats.push(node.attrs.list ? { list: node.attrs.list, indent: node.attrs.indent } : null);
      });
      const markers = listMarkers(formats);
      const decorations: Decoration[] = [];
      state.doc.forEach((node, offset, index) => {
        const marker = markers[index];
        if (marker) {
          decorations.push(
            Decoration.node(offset, offset + node.nodeSize, { "data-list-marker": marker }),
          );
        }
      });
      return DecorationSet.create(state.doc, decorations);
    },
  },
});

const focusKey = new PluginKey<boolean>("focus");

/** Keeps the selection visible while the properties panel has the focus. */
const inactiveSelectionPlugin = new Plugin<boolean>({
  key: focusKey,
  state: {
    init: () => true,
    apply: (tr, focused) => tr.getMeta(focusKey) ?? focused,
  },
  props: {
    decorations(state) {
      const focused = focusKey.getState(state);
      const { from, to, empty } = state.selection;
      if (focused || empty) return null;
      return DecorationSet.create(state.doc, [
        Decoration.inline(from, to, { style: "background-color: rgb(99 102 241 / 0.28)" }),
      ]);
    },
    handleDOMEvents: {
      focus: (view) => {
        view.dispatch(view.state.tr.setMeta(focusKey, true));
        return false;
      },
      blur: (view) => {
        view.dispatch(view.state.tr.setMeta(focusKey, false));
        return false;
      },
    },
  },
});

export interface RichCallbacks {
  context: () => RichContext;
  finish: () => void;
}

export function createRichState(element: TextElement, callbacks: RichCallbacks): EditorState {
  const withContext =
    (run: (view: EditorView, context: RichContext) => void): Command =>
    (_state, _dispatch, view) => {
      if (view) run(view, callbacks.context());
      return true;
    };
  const finish: Command = () => {
    callbacks.finish();
    return true;
  };
  return EditorState.create({
    doc: elementToDoc(element),
    plugins: [
      history(),
      inputRules({
        rules: [listRule(/^\s*([-*•])\s$/, "bullet"), listRule(/^(\d+)[.)]\s$/, "number")],
      }),
      keymap({
        "Mod-z": undo,
        "Shift-Mod-z": redo,
        "Mod-y": redo,
        "Mod-b": withContext(toggleBold),
        "Mod-i": withContext(toggleItalic),
        "Mod-u": withContext((view, context) => toggleDecoration(view, context, "underline")),
        "Shift-Mod-x": withContext((view, context) => toggleDecoration(view, context, "strike")),
        "Shift-Mod-8": withContext((view) => toggleList(view, "bullet")),
        "Shift-Mod-7": withContext((view) => toggleList(view, "number")),
        Tab: (state, dispatch) => {
          indentCommand(1)(state, dispatch);
          return true;
        },
        "Shift-Tab": (state, dispatch) => {
          indentCommand(-1)(state, dispatch);
          return true;
        },
        Enter: enter,
        Backspace: backspace,
        Escape: finish,
        "Mod-Enter": finish,
      }),
      keymap(baseKeymap),
      markersPlugin,
      inactiveSelectionPlugin,
      new Plugin({
        filterTransaction: (tr) => !tr.docChanged || textLength(tr.doc) <= TEXT_MAX_LENGTH,
      }),
    ],
  });
}

/** Plain text on the clipboard: paragraphs one per line (default would be blank lines). */
export function clipboardText(slice: Slice): string {
  return slice.content.textBetween(0, slice.content.size, "\n", "\n");
}

/** Selects the whole text (placeholder texts) or puts the caret at its end. */
export function initialSelection(state: EditorState, selectAll: boolean): Transaction {
  const end = state.doc.content.size - 1;
  return state.tr.setSelection(
    selectAll ? TextSelection.create(state.doc, 1, end) : TextSelection.create(state.doc, end),
  );
}

// ---------------------------------------------------------------------------
// Bridge to the properties panel
// ---------------------------------------------------------------------------

export interface RichTextBridge {
  elementId: string | null;
  view: EditorView | null;
  format: SelectionFormat | null;
  context: (() => RichContext) | null;
  set(partial: Partial<Omit<RichTextBridge, "set">>): void;
}

/** The text being edited, its selection's format and the commands for it. */
export const useRichText = create<RichTextBridge>()((set) => ({
  elementId: null,
  view: null,
  format: null,
  context: null,
  set: (partial) => set(partial),
}));

/**
 * Runs a formatting command on the text being edited, then gives the focus
 * back to it (buttons). Returns false when no text is being edited.
 */
export function withRichText(
  elementId: string,
  run: (view: EditorView, context: RichContext) => void,
  options: { focus?: boolean } = {},
): boolean {
  const { view, context, elementId: editing } = useRichText.getState();
  if (!view || !context || editing !== elementId) return false;
  run(view, context());
  if (options.focus !== false) view.focus();
  return true;
}
